/**
 * @file api-delivery-agreement.ts
 * @description API-клиент «доставки по договорённости» — F30, phase19.15 шаг 4.
 *
 * Зеркалит серверные контракты:
 *  - GET  /admin/orders/:id/delivery-agreement      — состояние заявки;
 *  - POST /admin/orders/:id/delivery-agreement/send — письмо клиенту с кнопкой;
 *  - POST /admin/orders/:id/delivery-charge         — ручная стоимость доставки.
 *
 * Подписи статусов — реестром (`DELIVERY_AGREEMENT_STATUS_META`): бейдж на
 * канбане, строка в реестре и заголовок блока в карточке заказа обязаны
 * говорить об одном состоянии одними словами. Новый статус на сервере = одна
 * запись здесь, а не ветка в трёх компонентах.
 */

import { ApiError, apiFetch } from "./api-client";
import type {
  OrderPaymentTerms,
  OrderReceiptMethod,
  OrderReceiptPayload,
  ShipmentCarrier,
} from "./api-orders";
import type { ShopPickupLocationDto } from "./api-shop-pickup";

// ─── DTO ────────────────────────────────────────────────────────────────────

/** Состояние заявки (enum `DeliveryAgreementStatus` сервера). */
export type DeliveryAgreementStatus =
  /** Заявка принята, менеджер ещё не выставил стоимость доставки. */
  | "AWAITING_CALCULATION"
  /** Стоимость выставлена и отправлена — ждём кнопку клиента. */
  | "AWAITING_CONFIRMATION"
  /** Клиент письменно подтвердил итоговую сумму (ПП РФ 2463 п. 13). */
  | "CONFIRMED"
  /** Заявка отменена таймером или менеджером. */
  | "CANCELLED"
  /** Заказ изменили после отправки — предложение недействительно. */
  | "OUTDATED";

/** Суммы заявки: товары, доставка и итог, который подтверждает клиент. */
export interface DeliveryAgreementTotalsDto {
  itemsTotal: string;
  /** `null` — стоимость доставки ещё не выставлена. */
  deliveryAmount: string | null;
  totalGross: string;
}

/**
 * Исход согласования: что именно мы предлагаем клиенту (phase19.24 шаг 5.1).
 *
 * Снапшот пункта самовывоза (`pickup`) приходит с сервера: адрес в письме и
 * адрес в карточке обязаны совпасть даже после правки настроек склада.
 */
export interface DeliveryAgreementReceiptDto {
  method: OrderReceiptMethod;
  /** Подпись способа по-русски из серверного реестра («Самовывоз»). */
  label: string;
  carrier: ShipmentCarrier | null;
  /** Название перевозчика по-русски; `null` — перевозчика нет. */
  carrierName: string | null;
  /** Куда ехать при самовывозе (снапшот пункта на момент согласования). */
  pickup: ShopPickupLocationDto | null;
  address: string | null;
  /**
   * Город и его код СДЭК из снапшота способа получения. Поля опциональны:
   * сервер добавляет их в шаге 5.1, а старые заказы отвечают без них — форма
   * обязана открываться и в этом случае.
   */
  city?: string | null;
  cityCode?: number | null;
  /** Стоимость доставки строкой; `null` — позиции-услуги нет. */
  deliveryAmount: string | null;
  comment: string | null;
}

/**
 * Кто выставил условия: витрина (выбор клиента при оформлении) или менеджер.
 *
 * От источника зависит вся подача блока «Доставка» (проект шага 5.2 §3):
 * выбор клиента показываем только на чтение с кнопкой «Изменить условия
 * доставки», условия менеджера — сразу формой.
 */
export type OrderReceiptSource = "SITE" | "MANAGER";

/**
 * Куда везём по выбранному тарифу (серверный `DeliveryQuoteDestination`).
 *
 * Город обязателен и непустой: сервер (`DeliveryQuoteDestinationDto`) не
 * принимает расчёт без города — заявку перевозчику по нему не создать.
 * Остальные поля читаются как `null`, а отправляются опущенными.
 */
export interface DeliveryQuoteDestination {
  city: string;
  cityCode?: number | null;
  postalCode?: string | null;
  pickupPointCode?: string | null;
  address?: string | null;
}

/** Одно место (коробка) снапшота: вес граммах, габариты в миллиметрах. */
export interface DeliveryQuotePackageSnapshot {
  /** Имя поля — как на сервере (`weightGrams`), а не как в отгрузке. */
  weightGrams: number;
  lengthMm?: number | null;
  widthMm?: number | null;
  heightMm?: number | null;
}

/** Куда едет посылка по тарифу: в пункт выдачи или курьером до двери. */
export type DeliveryQuoteMode = "PVZ" | "DOOR";

/**
 * Снапшот выбранного тарифа перевозчика (phase19.24 шаг 5.2 §6, п. 1).
 *
 * Едет вместе с исходом: сервер кладёт его в позицию-услугу «Доставка»
 * (`source: CALCULATED`) и в снапшот способа получения, а отгрузка забирает
 * оттуда тариф и назначение. Без снапшота кладовщик заново выбирал бы тариф
 * руками и мог отправить не тем, что согласовал клиент.
 */
export interface DeliveryQuoteSnapshot {
  carrier: ShipmentCarrier;
  tariffCode: number;
  tariffName: string;
  deliveryMode: DeliveryQuoteMode;
  /**
   * Цена перевозчика без наценки — расход магазина. СТРОКОЙ («389.00»), как и
   * все деньги: сервер держит её `Decimal`, и `number` терял бы копейки.
   */
  carrierPrice: string;
  periodMin?: number | null;
  periodMax?: number | null;
  /** Когда посчитали (ISO): тариф живёт недолго, дата это объясняет. */
  quotedAt: string;
  destination: DeliveryQuoteDestination;
  packages?: DeliveryQuotePackageSnapshot[];
}

/**
 * Снапшот способа получения из `GET /admin/orders/:id/delivery`
 * (phase19.24 шаг 5.2 §6, п. 2).
 *
 * Это тот же снапшот, что и в заявке, плюс три поля, без которых блок не
 * может выбрать подачу: чем считали (`quote`), кто выставил (`source`) и
 * когда (`setAt`).
 */
export interface OrderDeliveryReceiptDto extends DeliveryAgreementReceiptDto {
  /** Код ПВЗ из снапшота; `null` — везём по адресу или это самовывоз. */
  pickupPointCode?: string | null;
  /** Тариф, которым посчитали стоимость; `null` — ставили руками. */
  quote?: DeliveryQuoteSnapshot | null;
  source: OrderReceiptSource;
  /** Когда условия выставили (ISO); `null` — дата в снапшоте не сохранена. */
  setAt: string | null;
}

/**
 * Заявка в ответе `GET /admin/orders/:id/delivery` (серверный
 * `OrderDeliveryAgreementDto extends DeliveryAgreementAdminDto`).
 *
 * Это та же полная заявка, что и у `GET …/delivery-agreement`, плюс короткий
 * псевдоним канала. Поэтому вкладка «Доставка» обходится ОДНИМ запросом:
 * второй ответ про ту же заявку успевал разойтись с первым.
 */
export interface OrderDeliveryAgreementDto extends DeliveryAgreementDto {
  /** Дубль `agreementChannel` под коротким именем — так его отдаёт сервер. */
  channel: OrderAgreementChannel | null;
}

/**
 * Минимум о заявке, по которому считается состояние блока «Доставка».
 *
 * Общий знаменатель полной заявки и её урезанного вида из ответа о доставке:
 * реестр состояний обязан работать с любым из двух источников, не требуя сумм.
 */
export interface DeliveryAgreementStateView {
  status: DeliveryAgreementStatus;
  calculatedAt: string | null;
  confirmedAt: string | null;
  expiresAt: string | null;
}

/**
 * Почему условия нельзя менять (ответ `GET /admin/orders/:id/delivery`).
 *
 * `paid`/`partially_paid` — деньги получены, условия заморожены (F26);
 * `closed` — заказ завершён или отменён; `no_permission` — у роли нет права
 * вести заказы; `stage` — заказ на стадии, где условия доставки не правятся
 * (сборка, отгрузка, «Заявка» до перевода в согласование). Реестр текстов —
 * `delivery-block-state.ts`.
 */
export type DeliveryLockReason =
  | "paid"
  | "partially_paid"
  | "closed"
  | "no_permission"
  | "stage";

/**
 * Единственный путь дальше, когда условия закрыты стадией (ответ
 * `GET /admin/orders/:id/delivery`, поле `nextStep`).
 *
 * `open_agreement` — заказ на «Заявке»: условия правятся только после
 * перевода в согласование доставки (стадия `PENDING_DELIVERY_AGREEMENT`), и
 * блок обязан показать именно этот путь, а не форму, которую сервер отвергнет.
 */
export type OrderDeliveryNextStep = "open_agreement";

/** Состояние заявки для блока в карточке заказа. */
export interface DeliveryAgreementDto {
  status: DeliveryAgreementStatus;
  requestedAt: string | null;
  calculatedAt: string | null;
  confirmedAt: string | null;
  totals: DeliveryAgreementTotalsDto;
  /** Можно ли прямо сейчас отправить письмо с кнопкой подтверждения. */
  canSend: boolean;
  /** Почему нельзя — человеческим языком, прямо в подсказку кнопки. */
  reason: string | null;
  /** Ссылка подтверждения (менеджер может продиктовать); `null` — не отправляли. */
  confirmUrl: string | null;
  /** Докогда действует ссылка (ISO); `null` — стоимость не выставлена. */
  expiresAt: string | null;
  /** Как называется выставленная доставка («Доставка (Почта России)»). */
  deliveryLabel: string | null;

  /*
   * phase19.24 шаг 5.1 — предмет согласования: способ получения, условия
   * оплаты и канал (письмо или телефон).
   */
  /** Что предлагаем клиенту; `null` — исход не выбран (или его выставила витрина). */
  receipt: DeliveryAgreementReceiptDto | null;
  /** Как платит клиент: предоплата или при получении — часть предмета согласования. */
  paymentTerms: OrderPaymentTerms;
  /**
   * Канал согласования: `PHONE` — письмо не отправляли, ссылку менеджер
   * диктует (у клиента нет e-mail). Без этой пометки «письмо не отправлено»
   * читалось бы как сбой отправки.
   */
  agreementChannel: OrderAgreementChannel | null;
}

/** Чем согласуем: письмом с кнопкой или голосом с диктовкой ссылки (§7 п.3). */
export type OrderAgreementChannel = "EMAIL" | "PHONE";

/** Ответ на ручную стоимость доставки: позиция-услуга + новые итоги заказа. */
export interface OrderDeliveryChargeDto {
  orderItemId: string;
  carrier: ShipmentCarrier;
  carrierName: string;
  priceGross: string;
  vatRate: number;
  vatAmount: string;
  comment: string | null;
  orderTotalGross: string;
  orderTotalVat: string;
  /** Правка отозвала отправленное предложение — нужна повторная отправка. */
  agreementReset: boolean;
}

/** Тело POST /admin/orders/:id/delivery-charge. */
export interface SetOrderDeliveryChargePayload {
  carrier: ShipmentCarrier;
  /** Рубли, две цифры после запятой; сервер не принимает 0. */
  amount: number;
  comment?: string;
}

// ─── Запросы ────────────────────────────────────────────────────────────────

/** Состояние заявки по заказу. Работает и для обычного заказа (пустая заявка). */
export function getDeliveryAgreement(orderId: string): Promise<DeliveryAgreementDto> {
  return apiFetch<DeliveryAgreementDto>(`/admin/orders/${orderId}/delivery-agreement`);
}

/**
 * «Отправить на подтверждение»: письмо клиенту с кнопкой.
 * Повторный вызов выпускает новый токен — старая ссылка перестаёт работать.
 */
export function sendDeliveryAgreement(orderId: string): Promise<DeliveryAgreementDto> {
  return apiFetch<DeliveryAgreementDto>(
    `/admin/orders/${orderId}/delivery-agreement/send`,
    { method: "POST" },
  );
}

/** Тело POST /admin/orders/:id/delivery-agreement/outcome. */
export interface DeliveryAgreementOutcomePayload extends OrderReceiptPayload {
  paymentTerms: OrderPaymentTerms;
  /**
   * Тариф, которым посчитали стоимость (phase19.24 шаг 5.2).
   *
   * Необязателен: перевозчика без интеграции считают руками, и снапшота у
   * такого исхода не бывает. Есть снапшот — позиция-услуга «Доставка»
   * становится `CALCULATED`, а отгрузка берёт тариф и назначение оттуда.
   */
  quote?: DeliveryQuoteSnapshot;
}

/**
 * «Отменить заявку» из меню «⋯»: предложение снимается, ссылка перестаёт
 * работать, заказ остаётся на стадии согласования — условия можно выставить
 * заново (проект §3, состояние `CANCELLED`).
 */
export function cancelDeliveryAgreement(orderId: string): Promise<DeliveryAgreementDto> {
  return apiFetch<DeliveryAgreementDto>(
    `/admin/orders/${orderId}/delivery-agreement/cancel`,
    { method: "POST" },
  );
}

/** Результат «Сохранить исход»: способ, позиция-услуга и новые итоги заказа. */
export interface DeliveryAgreementOutcomeResultDto {
  receipt: DeliveryAgreementReceiptDto | null;
  paymentTerms: OrderPaymentTerms;
  /** Позиция-услуга «Доставка»; `null` — исход её не требует (самовывоз). */
  orderItemId: string | null;
  deliveryAmount: string | null;
  vatRate: number;
  vatAmount: string;
  deliveryLabel: string | null;
  orderTotalGross: string;
  orderTotalVat: string;
  /** Правка отозвала отправленное предложение — нужна повторная отправка. */
  agreementReset: boolean;
}

/**
 * «Сохранить исход»: что именно мы предлагаем клиенту (phase19.24 шаг 5.1).
 *
 * Пришло на смену `POST /delivery-charge`: исходов четыре, а не один —
 * `PICKUP` удаляет позицию-услугу «Доставка», `SHOP_COURIER` принимает 0 ₽,
 * `AGREEMENT` оставляет сумму неизвестной. Сервер сам говорит, отозвалось ли
 * отправленное предложение (`agreementReset`) — без этого менеджер ждал бы
 * подтверждения по ссылке, которой больше нет.
 */
export function setDeliveryAgreementOutcome(
  orderId: string,
  payload: DeliveryAgreementOutcomePayload,
): Promise<DeliveryAgreementOutcomeResultDto> {
  return apiFetch<DeliveryAgreementOutcomeResultDto>(
    `/admin/orders/${orderId}/delivery-agreement/outcome`,
    { method: "POST", json: payload },
  );
}

/**
 * Ручная стоимость доставки для перевозчика без интеграции.
 *
 * @deprecated phase19.24 шаг 5.1 — используйте `setDeliveryAgreementOutcome`:
 * стоимость без способа получения оставляла заказ без адреса и графика.
 */
export function setOrderDeliveryCharge(
  orderId: string,
  payload: SetOrderDeliveryChargePayload,
): Promise<OrderDeliveryChargeDto> {
  return apiFetch<OrderDeliveryChargeDto>(`/admin/orders/${orderId}/delivery-charge`, {
    method: "POST",
    json: payload,
  });
}

// ─── Реестр статусов ────────────────────────────────────────────────────────

/** Как рисовать бейдж: вариант из shadcn либо свои классы (синего варианта нет). */
export interface DeliveryAgreementStatusMeta {
  /** Короткая подпись бейджа в реестре и на канбане. */
  badge: string;
  /** Полная подпись в карточке заказа. */
  title: string;
  variant: "warning" | "success" | "destructive" | "outline";
  /** Доп. классы — для «синего» состояния, которого нет в вариантах бейджа. */
  className?: string;
}

export const DELIVERY_AGREEMENT_STATUS_META: Record<
  DeliveryAgreementStatus,
  DeliveryAgreementStatusMeta
> = {
  AWAITING_CALCULATION: {
    badge: "Ждёт расчёта",
    title: "Ждёт расчёта доставки",
    variant: "warning",
  },
  AWAITING_CONFIRMATION: {
    badge: "Ждём клиента",
    title: "Отправлено клиенту, ждём подтверждения",
    variant: "outline",
    // Синий = «мяч на стороне клиента»: отличается и от янтарного «мы должны
    // посчитать», и от зелёного «согласовано».
    className: "bg-blue-500/12 text-blue-700 [box-shadow:none]",
  },
  CONFIRMED: {
    // Без «✓» (SC43, phase19.31): статус пишется словом, цвет даёт тон.
    badge: "Подтверждено",
    title: "Клиент подтвердил итоговую стоимость",
    variant: "success",
  },
  CANCELLED: {
    badge: "Заявка отменена",
    // Не повтор бейджа: подпись обязана добавлять к состоянию ДЕЙСТВИЕ —
    // отменённая заявка не закрывает заказ, условия выставляют заново
    // (дефект QA 5.1: «Заявка отменена · Заявка отменена» двумя строками).
    title: "Условия нужно выставить заново",
    variant: "outline",
  },
  OUTDATED: {
    badge: "Устарело",
    title: "Предложение устарело — отправьте заново",
    variant: "destructive",
  },
};

// ─── Реестр отказов сервера ─────────────────────────────────────────────────

/**
 * Отказы сервера на действиях заявки — человеческим языком.
 *
 * Реестр, а не `if` в компонентах: одни и те же коды приходят и на
 * «Сохранить исход», и на «Отправить на подтверждение», и объяснять их надо
 * одинаково. Кода в реестре нет — показываем сообщение сервера: оно уже
 * написано для менеджера.
 */
export const DELIVERY_AGREEMENT_ERROR_TEXT: Record<string, string> = {
  // 422 при отправке письма по заявке без выставленного исхода (ревью 5.1).
  receipt_required:
    "Сначала выставьте способ получения и стоимость — клиенту пока нечего подтверждать.",
  pickup_location_missing:
    "У склада магазина нет адреса: заполните пункт самовывоза в настройках складов.",
  customer_email_missing:
    "У клиента нет e-mail: добавьте адрес в карточке клиента или согласуйте по телефону.",
  carrier_requires_prepay:
    "Доставка ТК идёт только по предоплате — поменяйте условия оплаты (F31).",
  delivery_agreement_confirmed:
    "Клиент уже подтвердил условия — изменить их можно только новым согласованием.",
  // Заказ завершён или отменён: условия доставки остаются историей.
  order_terminal: "Заказ закрыт — способ получения и доставку менять уже нельзя.",
  receipt_method_not_allowed:
    "Выберите способ получения: «согласуем потом» из вариантов убран — клиенту нужно знать, как он получит заказ.",
  // 422 на исходе: приложен расчёт, а стоимость для клиента пустая (ревью
  // 5.2). Сервер не принимает тариф без суммы — иначе в письме стояло бы
  // «доставка не выставлена» при рассчитанном тарифе.
  receipt_amount_required:
    "Не указана стоимость доставки для клиента: подставьте её из расчёта или впишите вручную.",
  // Правка условий обязана вернуть заказ на согласование, а подходящей стадии
  // в воронке магазина нет: молча оставить заказ в сборке нельзя.
  agreement_reset_failed:
    "Не удалось вернуть заказ на согласование — проверьте воронку стадий.",
  // 409 на исходе, когда деньги уже получены (шаг 5.2 §6 п. 3): попытка
  // поменять способ и адрес замороженного заказа.
  receipt_locked_paid:
    "Условия заморожены после оплаты: способ, адрес и стоимость не меняем. " +
    "Нужны другие условия — отмените заказ и создайте новый.",
  // 422 на создании отгрузки по заказу с самовывозом (шаг 5.2 §5).
  shipment_not_needed_pickup:
    "Заказ на самовывоз: отгрузка перевозчиком не нужна — клиент заберёт заказ в пункте магазина.",
  shipment_carrier_required:
    "Укажите перевозчика: условия доставки по заказу ещё не согласованы, подставить их неоткуда.",

  /*
   * Отказы по приложенному расчёту (шаг 5.2, `checkDeliveryQuote`). Все они
   * означают одно: тариф в карточке выглядел бы согласованным, а заявку
   * перевозчику по нему не создать — поэтому объясняем, что именно поправить.
   */
  receipt_quote_invalid:
    "Расчёт неполный: выберите тариф заново — по нему не собрать заявку перевозчику.",
  receipt_quote_carrier_mismatch:
    "Расчёт сделан для другого перевозчика: пересчитайте доставку выбранным перевозчиком.",
  receipt_quote_city_required:
    "В расчёте нет города получателя: укажите город и пересчитайте тариф.",
  receipt_quote_pickup_point_required:
    "Для доставки в пункт выдачи нужен его код: выберите ПВЗ и пересчитайте тариф.",
};

/** Текст отказа для тоста: реестр → сообщение сервера → общая фраза. */
export function deliveryAgreementErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return DELIVERY_AGREEMENT_ERROR_TEXT[err.code] ?? err.message ?? fallback;
  }
  return fallback;
}

/**
 * Строка состояния в блоке заявки. Отдельно от реестра, потому что для
 * «ждём подтверждения» в текст входит срок жизни ссылки, а для «подтверждено» —
 * дата и сумма (их подставляет сам компонент).
 */
export function deliveryAgreementStatusText(
  status: DeliveryAgreementStatus,
  expiresAtText: string | null,
): string {
  if (status === "AWAITING_CONFIRMATION" && expiresAtText) {
    return `Отправлено клиенту, ждём подтверждения до ${expiresAtText}`;
  }
  return DELIVERY_AGREEMENT_STATUS_META[status].title;
}
